import { useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ToastContext } from '@/components/common/CommonToast';
import { CustomDataTable } from '@/components/common/ui/table/CustomDataTable';
import { useTranslation } from '@/config/i18n';
import useFetchClient from '@/hooks/useFetchClient';
import usePagination from '@/hooks/usePagination';
import useSearch from '@/hooks/useSearch';
import { toastErrorMessage, toastMessage } from '@/lib/utils';
import PATH from '@/routes/path';
import { apiRequest } from '@/services';
import { DEFAULT_PAGE, DEFAULT_PER_PAGE } from '@/shared/constants/pagination';
import { Pagination } from '@/types/pagination';
import { PlanColumns } from '../table/PlanColumns';
import { PlanResponseType } from '@/types/plan';
import { planStatus } from '@/shared/constants/plan';
import { useLoading } from '@/components/common/loader/LoadingContext';
import { HTTP_STATUS } from '@/shared/constants';
import DialogCustom from '@/components/common/ui/dialog';
import ButtonCustom from '@/components/common/ui/button';

type Props = {
  searchTrigger: number;
}

const PlanList: React.FC<Props> = ({ searchTrigger }) => {
  const [t] = useTranslation('');
  const { apiService } = apiRequest();
  const { searchParams } = useSearch();
  const { toast } = useContext(ToastContext);
  const { query } = Object.fromEntries(searchParams.entries());
  const navigate = useNavigate();
  const { setLoadingGlobal } = useLoading();

  const [selectedPlan, setSelectedPlan] = useState<any>();
  const [dialogChangeStatusVisible, setDialogChangeStatusVisible] =
    useState(false);

  const { data: plans, error } = useFetchClient(
    async () => await apiService.plans.query(query, params),
    [query, searchTrigger],
    {
      isLoadingGlobal: true,
      isScrollToTop: true,
    },
  );

  const params = useMemo(() => {
    return query
      ? undefined
      : { limit: DEFAULT_PER_PAGE, paginate: DEFAULT_PAGE };
  }, [query]);

  if (error) {
    toast?.current?.show(toastErrorMessage(t('common.toast.fail')));
  }

  const { paginationComputed, setPagination } = usePagination(
    plans?.pagination,
  );

  function handleEditPlan(id: number) {
    navigate(
      `${PATH.plan}/${PATH.plan_edit}/${id}`,
    );
  }

  const handleError = (error: any) => {
    const { status_code, errors } = error;
    if (status_code === HTTP_STATUS.FORM_ERROR) {
      const errorLimitStudent = errors?.find(
        (e: any) => e.error_code === 'MFS-E-LEARNING-ADMIN-USER-169',
      );
      if (errorLimitStudent) {
        toast?.current?.show(toastErrorMessage(errorLimitStudent?.message));
      }
    }
  };

  async function handleEditStatusPlan(
    isActive: boolean,
    rowData: PlanResponseType,
  ) {
    setSelectedPlan({ isActive, rowData });

    const { id } = rowData;

    const nextDialogVisibleValue = !dialogChangeStatusVisible;
    setDialogChangeStatusVisible(nextDialogVisibleValue);

    if (nextDialogVisibleValue) return;

    setLoadingGlobal(true);
    const { data: plan, error: planError } = await apiService.plans.update(id, {
      ...rowData,
      status: isActive ? planStatus.PUBLIC : planStatus.DRAFT,
    });
    setLoadingGlobal(false);
    if (planError) {
      handleError(planError);
      return;
    }

    const indexUpdate = plans.data.findIndex((plan: any) => plan.id === id);
    if (indexUpdate !== -1) {
      plans.data[indexUpdate].status = plan.data.status;
    }

    const msg =
      plan.data.status === planStatus.PUBLIC
        ? t('plan_list.toast.public_plan_success')
        : t('plan_list.toast.save_draft_plan_success');
    toast?.current?.show(toastMessage(msg));
  }

  function onPaginationChanged(paginationState: Pagination) {
    setPagination({
      ...paginationComputed,
      paginate: paginationState.paginate,
      limit: paginationState.limit,
    });
  }

  function onSortChanged(paginationState: Pagination) {
    setPagination(paginationState);
  }

  return (
    <>
      <CustomDataTable
        wrapClassName="mt-[20px]"
        values={plans?.data ?? []}
        columns={PlanColumns(t, handleEditPlan, handleEditStatusPlan, paginationComputed)}
        pagination={paginationComputed}
        onPaginationChanged={onPaginationChanged}
        onSortChanged={onSortChanged}
      />

      <DialogCustom
        header={
          selectedPlan?.isActive
            ? t('plan_list.modal.change_status_public_to_draft.title')
            : t('plan_list.modal.change_status_draft_to_public.title')
        }
        visible={dialogChangeStatusVisible}
        className='!w-[400px]'
        headerClassName="border-b border-gray-200 !py-[13px]"
        contentClassName="border-b border-gray-200 !p-[16px] !text-[16px] !leading-[24px] text-center text-gray-900"
        onHide={() => setDialogChangeStatusVisible(false)}
        footer={
          <div className="flex justify-end gap-4">
            <ButtonCustom
              type="button"
              className="!rounded-md"
              severity="secondary"
              onClick={() => setDialogChangeStatusVisible(false)}
            >
              {selectedPlan?.isActive
                ? t('plan_list.modal.change_status_public_to_draft.cancel')
                : t('plan_list.modal.change_status_draft_to_public.cancel')}
            </ButtonCustom>
            <ButtonCustom
              type="button"
              className="!rounded-md"
              onClick={() =>
                handleEditStatusPlan(
                  selectedPlan.isActive,
                  selectedPlan.rowData,
                )
              }
            >
              {selectedPlan?.isActive
                ? t('plan_list.modal.change_status_public_to_draft.yes')
                : t('plan_list.modal.change_status_draft_to_public.yes')}
            </ButtonCustom>
          </div>
        }
      >
        {!selectedPlan?.isActive
          ? t('plan_list.modal.change_status_public_to_draft.content')
          : t('plan_list.modal.change_status_draft_to_public.content')}
      </DialogCustom>
    </>
  );
};

export default PlanList;
